// src/lib/math-text.ts
//
// Issue #711: ~87 questions use caret exponent notation that can't be
// converted to unicode superscripts (variable, parenthesized, negative, or
// fractional exponents — 2^x, 3·2^(kx), m^(1/3), x^-1). Rather than rewrite
// the stored content or pull in KaTeX, we transform caret notation to real
// superscripts at RENDER time. This is presentation-only — the stored text
// is untouched — and it fixes the entire class (plus any future caret
// content) wherever <MathText> is used.
//
// Pure parser here; the React wrapper is src/components/MathText.tsx.

export type MathSegment = { sup: boolean; text: string }

const EXP_TOKEN = /[a-zA-Z0-9.]/

/**
 * Split a string into plain + superscript segments, recognizing
 * `base^exponent` where the exponent is one of:
 *   - parenthesized: 2^(kx), e^(2x-1), m^(1/3)  → the inside becomes the sup
 *   - a signed run:  2^x, b^t, 2^10, x^-1        → that run becomes the sup
 * A bare `^` with no valid exponent after it is left as literal text.
 */
export function parseMathText(text: string): MathSegment[] {
  const segs: MathSegment[] = []
  let buf = ''
  let i = 0

  const flush = () => {
    if (buf) {
      segs.push({ sup: false, text: buf })
      buf = ''
    }
  }

  while (i < text.length) {
    if (text[i] === '^' && i + 1 < text.length) {
      let j = i + 1
      let exp = ''

      if (text[j] === '(' || text[j] === '{') {
        // Parenthesized exponent — capture to the matching close paren. LaTeX
        // braces (`2^{x+1}`, as Sage writes) group the same way; without this
        // the parser read only `x` as the exponent and left `+1` on the
        // baseline, changing the expression.
        const open = text[j]
        const close = open === '(' ? ')' : '}'
        let depth = 0
        const start = j
        let closed = false
        for (; j < text.length; j++) {
          if (text[j] === open) depth++
          else if (text[j] === close) {
            depth--
            if (depth === 0) {
              j++
              closed = true
              break
            }
          }
        }
        if (closed) exp = text.slice(start + 1, j - 1) // strip the outer parens
      } else {
        // Optional leading minus, then a run of alphanumerics / dot.
        let k = j
        if (text[k] === '-') k++
        while (k < text.length && EXP_TOKEN.test(text[k])) k++
        // A dot belongs to the exponent only as a decimal point, i.e. with a
        // digit after it. "The value is 2^3." ends a sentence — the period is
        // not part of the exponent (it drew as 2^(3.)) (build-7 retest 9, F4).
        while (k > j && text[k - 1] === '.') k--
        if (k > j && /[a-zA-Z0-9]/.test(text[j] === '-' ? (text[j + 1] ?? '') : text[j])) {
          exp = text.slice(j, k)
          j = k
        }
      }

      if (exp.length > 0) {
        flush()
        segs.push({ sup: true, text: exp })
        i = j
        continue
      }
    }
    buf += text[i]
    i++
  }
  flush()
  return segs
}

/* ------------------------------------------------------------------ */
/*  Drawing it                                                         */
/* ------------------------------------------------------------------ */
//
// React Native has no <sup>, and a nested <Text> can be made smaller but not
// raised off the baseline. A small exponent sitting ON the baseline reads as a
// coefficient: "2ˣ" drawn that way is "2x" — a product, a different value. And
// a renderer that forgot the smaller style drew it full size: exactly "2x".
// (That is how Sage's question card turned 2^x into 2x.)
//
// So the app draws exponents as Unicode superscript characters, which sit in
// the right place in every font, at every size, in every container. Where an
// exponent contains a character with no superscript form (a "/" in m^(1/3), a
// capital letter), it falls back to showing the caret EXPLICITLY — "m^(1/3)" —
// which is always unambiguous. Never a lookalike that changes the maths.

const SUPERSCRIPT: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '+': '⁺',
  '-': '⁻',
  '=': '⁼',
  '(': '⁽',
  ')': '⁾',
  a: 'ᵃ',
  b: 'ᵇ',
  c: 'ᶜ',
  d: 'ᵈ',
  e: 'ᵉ',
  f: 'ᶠ',
  g: 'ᵍ',
  h: 'ʰ',
  i: 'ⁱ',
  j: 'ʲ',
  k: 'ᵏ',
  l: 'ˡ',
  m: 'ᵐ',
  n: 'ⁿ',
  o: 'ᵒ',
  p: 'ᵖ',
  r: 'ʳ',
  s: 'ˢ',
  t: 'ᵗ',
  u: 'ᵘ',
  v: 'ᵛ',
  w: 'ʷ',
  x: 'ˣ',
  y: 'ʸ',
  z: 'ᶻ',
}

/**
 * An exponent as it should be DRAWN in plain text: Unicode superscripts when
 * every character has one ("x" → "ˣ", "-1" → "⁻¹", "x+1" → "ˣ⁺¹"), otherwise
 * explicit caret notation ("1/3" → "^(1/3)", "N" → "^N").
 */
export function superscriptText(exp: string): string {
  const compact = exp.replace(/\s+/g, '')
  const chars = [...compact]
  if (chars.length > 0 && chars.every((ch) => ch in SUPERSCRIPT)) {
    return chars.map((ch) => SUPERSCRIPT[ch]).join('')
  }
  return chars.length > 1 ? `^(${compact})` : `^${compact}`
}

/**
 * The whole string with every exponent drawn by superscriptText — what any
 * plain-text surface (a native <Text>, a notification, a share sheet) shows.
 */
export function drawMathText(text: string): string {
  return parseMathText(text)
    .map((seg) => (seg.sup ? superscriptText(seg.text) : seg.text))
    .join('')
}

/** True if the text contains caret notation we'd transform. */
export function hasCaretMath(text: string): boolean {
  return parseMathText(text).some((s) => s.sup)
}

/* ------------------------------------------------------------------ */
/*  Saying it out loud                                                 */
/* ------------------------------------------------------------------ */
//
// A superscript carries its meaning in POSITION, which is exactly the thing a
// screen reader and a speech synthesizer both throw away. The segments above
// concatenate with no separator, so the visible "x⁻¹" flattens to the text
// "x-1" — read aloud as "x minus 1", a DIFFERENT VALUE, not merely an unclear
// one. "2^x" flattens to "2x", a product. "m^(1/3)" flattens to "m1/3".
//
// So every surface that renders a superscript also needs a spoken form. This
// lives here, next to the parser, because the web renderers and the React
// Native ones must not drift into saying different things about the same
// question.

const ORDINAL_POWERS: Record<string, string> = {
  '2': 'squared',
  '3': 'cubed',
}

const FRACTION_WORDS: Record<string, string> = {
  '1/2': 'one half',
  '1/3': 'one third',
  '2/3': 'two thirds',
  '1/4': 'one fourth',
  '3/4': 'three fourths',
}

/**
 * How one exponent should be SAID. "2" → "squared", "-1" → "to the power of
 * negative 1", "kx" → "to the power of k x".
 *
 * Deliberately verbose over clever: "to the power of" is unambiguous in every
 * synthesizer we have no control over, where a terser "super 1" is not.
 */
export function speakMathSegment(exp: string): string {
  const e = exp.trim()
  if (ORDINAL_POWERS[e]) return ORDINAL_POWERS[e]
  if (FRACTION_WORDS[e]) return `to the power of ${FRACTION_WORDS[e]}`
  // A leading minus is "negative", not "minus": "minus" reads as subtraction
  // from the base, which is the very confusion this function exists to remove.
  const negative = e.startsWith('-')
  const body = negative ? e.slice(1) : e
  // Split a variable run so "kx" is spoken "k x" rather than as a word.
  const spokenBody = /^[a-zA-Z]{2,}$/.test(body) ? body.split('').join(' ') : body
  return `to the power of ${negative ? 'negative ' : ''}${spokenBody}`
}

/**
 * The whole string as it should be SPOKEN, superscripts expanded in place.
 * `parseMathText` decides what a superscript is; this only decides how to say
 * one, so the two can never disagree about where the exponents are.
 */
export function spokenMathText(text: string): string {
  return parseMathText(text)
    .map((seg) => (seg.sup ? ` ${speakMathSegment(seg.text)} ` : seg.text))
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
}
